/**
 * Hook React Query pour les membres de l'équipe
 * Récupère les membres + leur dernière attestation
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useEtablissement } from '@/contexts/EtablissementContext';

export interface Membre {
  id: string;
  prenom: string;
  nom: string;
  email: string;
  poste: string | null;
  type_contrat: string;
  date_embauche: string | null;
  date_depart: string | null;
  statut_archivage: string;
  created_at: string | null;
  updated_at: string | null;
  etablissement_id: string;
  // Attestation jointe (la plus récente)
  attestation?: {
    id: string;
    statut: string;
    date_delivrance: string | null;
    date_expiration: string | null;
    date_renouvellement: string | null;
    storage_path: string | null;
    nom_fichier: string | null;
    verified_at: string | null;
    mise_en_demeure_envoyee_at: string | null;
    // Horodatage précis de la vérification gouvernementale (Railway),
    // distinct de attestations.verified_at qui ne stocke que la date.
    verification_verified_at: string | null;
    // Date de réception réelle du document par l'établissement — distincte de
    // date_delivrance (délivrance administrative). Écrite une seule fois.
    received_at: string | null;
  } | null;
  // Campagne de prise de référence liée (optionnel)
  campaign_status?: string | null;
}

export interface EquipeStats {
  total: number;
  conformes: number;
  en_attente: number;
  verification_en_cours: number;
  a_regulariser: number;
}

export type AttestationStatus = 'conforme' | 'en_attente' | 'verification_en_cours' | 'expiree' | 'a_regulariser';

/**
 * Détermine le statut d'attestation d'un membre
 * 
 * Statuts DB possibles (table attestations.statut) :
 * - 'en_attente' : demande envoyée, aucun document reçu
 * - 'en_cours_verification' : ancien statut intermédiaire, conservé pour compatibilité
 * - 'en_attente_verification' : statut intermédiaire canonique après upload / extraction / contrôle
 * - 'conforme' : vérifié et valide
 * - 'expiree' : date de renouvellement dépassée
 * - 'refusee' / 'erreur_traitement' / 'a_regulariser' : non conforme
 */
export function getAttestationStatus(membre: Membre): AttestationStatus {
  if (!membre.attestation) return 'a_regulariser';
  
  const { statut, date_renouvellement } = membre.attestation;
  
  if (statut === 'conforme') {
    // Vérifier si le renouvellement est dépassé
    if (date_renouvellement) {
      const now = new Date();
      const renouvellement = new Date(date_renouvellement);
      if (renouvellement < now) return 'expiree';
    }
    return 'conforme';
  }
  
  if (statut === 'en_attente') return 'en_attente';
  if (statut === 'en_cours_verification' || statut === 'en_attente_verification') return 'verification_en_cours';
  if (statut === 'expiree') return 'expiree';
  if (statut === 'refusee' || statut === 'erreur_traitement') return 'a_regulariser';
  
  return 'a_regulariser';
}

/**
 * Indique si un membre a une vérification d'attestation en cours
 * (utile pour déclencher un polling automatique côté UI)
 */
export function hasVerificationInProgress(membres: Membre[]): boolean {
  return membres.some(m => getAttestationStatus(m) === 'verification_en_cours');
}

/**
 * Calcule les statistiques de l'équipe
 */
export function computeEquipeStats(membres: Membre[]): EquipeStats {
  const stats: EquipeStats = { total: 0, conformes: 0, en_attente: 0, verification_en_cours: 0, a_regulariser: 0 };
  
  for (const membre of membres) {
    if (membre.statut_archivage !== 'actif') continue;
    stats.total++;
    
    const status = getAttestationStatus(membre);
    if (status === 'conforme') stats.conformes++;
    else if (status === 'en_attente') stats.en_attente++;
    else if (status === 'verification_en_cours') stats.verification_en_cours++;
    else stats.a_regulariser++; // expiree + a_regulariser
  }
  
  return stats;
}

/**
 * Hook principal pour charger les membres avec leur attestation
 */
export function useMembresQuery() {
  const { user, profile } = useAuth();
  const { etablissement } = useEtablissement();
  const orgId = profile?.organization_id;
  const etablissementId = etablissement?.id;
  
  // En V1, on peut lancer la query dès qu'on a l'orgId (car 1 seul établissement)
  // L'établissement_id sera utilisé si dispo, sinon on filtre par org via la jointure
  const isReady = !!user?.id && !!orgId;
  
  const queryKey = useMemo(
    () => ['membres', etablissementId ?? orgId ?? ''],
    [etablissementId, orgId]
  );
  
  const query = useQuery({
    queryKey,
    // Polling automatique : tant qu'une attestation est en cours de vérification,
    // on rafraîchit toutes les 5s pour refléter le passage au statut final
    refetchInterval: (q) => {
      const data = q.state.data as Membre[] | undefined;
      if (data && hasVerificationInProgress(data)) return 5000;
      return false;
    },
    queryFn: async (): Promise<Membre[]> => {
      if (!orgId) throw new Error('Organization ID missing');
      
      let q = supabase
        .from('membres')
        .select(`
          *,
          attestations (
            id,
            statut,
            date_delivrance,
            date_expiration,
            date_renouvellement,
            storage_path,
            nom_fichier,
            verified_at,
            mise_en_demeure_envoyee_at,
            received_at,
            attestation_verifications (
              verified_at,
              created_at
            )
          )
        `)
        .eq('statut_archivage', 'actif')
        .order('created_at', { ascending: false });
      
      // Si on a l'établissement_id, filtrer directement
      if (etablissementId) {
        q = q.eq('etablissement_id', etablissementId);
      } else {
        // Fallback V1 : récupérer l'établissement de l'org
        const { data: etab } = await supabase
          .from('etablissements')
          .select('id')
          .eq('organization_id', orgId)
          .eq('actif', true)
          .limit(1)
          .single();
        
        if (etab) {
          q = q.eq('etablissement_id', etab.id);
        } else {
          return []; // Pas d'établissement → pas de membres
        }
      }
      
      const { data: membres, error } = await q;
      
      if (error) throw error;
      
      // Normaliser : prendre la dernière attestation pour chaque membre
      return (membres || []).map(membre => {
        const attestations = (membre.attestations as any[]) || [];
        // Prendre l'attestation la plus récente (triée par date_delivrance desc)
        const latestAttestation = attestations.length > 0
          ? attestations.sort((a, b) => {
              const dateA = a.date_delivrance ? new Date(a.date_delivrance).getTime() : 0;
              const dateB = b.date_delivrance ? new Date(b.date_delivrance).getTime() : 0;
              return dateB - dateA;
            })[0]
          : null;

        // Récupérer l'horodatage précis de la dernière vérification gouvernementale
        // (attestation_verifications.verified_at), distinct de la date simple
        // stockée sur attestations.verified_at.
        const verifications = (latestAttestation?.attestation_verifications as any[]) || [];
        const latestVerification = verifications.length > 0
          ? verifications.sort((a, b) =>
              new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            )[0]
          : null;

        const attestationWithVerification = latestAttestation
          ? {
              ...latestAttestation,
              verification_verified_at: latestVerification?.verified_at ?? null,
            }
          : null;

        return {
          id: membre.id,
          prenom: membre.prenom,
          nom: membre.nom,
          email: membre.email,
          poste: membre.poste,
          type_contrat: membre.type_contrat,
          date_embauche: membre.date_embauche,
          date_depart: membre.date_depart,
          statut_archivage: membre.statut_archivage,
          created_at: membre.created_at,
          updated_at: membre.updated_at,
          etablissement_id: membre.etablissement_id,
          attestation: attestationWithVerification,
        };
      });
    },
    enabled: isReady,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  // Calculer les stats à partir des données
  const stats = useMemo(() => {
    if (!query.data) return { total: 0, conformes: 0, en_attente: 0, a_regulariser: 0 };
    return computeEquipeStats(query.data);
  }, [query.data]);
  
  return {
    ...query,
    isLoading: query.isLoading,
    isFetched: query.isFetched,
    stats,
  };
}
